import { useEffect } from "react";
import { isTextInput } from "./isTextInput";
function parseCombo(combo) {
    const parts = combo.toLowerCase().split("+").map((p)=>p.trim()).filter(Boolean);
    const result = {
        key: "",
        mod: false,
        shift: false,
        alt: false
    };
    for (const part of parts){
        if (part === "mod" || part === "cmd" || part === "ctrl" || part === "meta") result.mod = true;
        else if (part === "shift") result.shift = true;
        else if (part === "alt" || part === "option") result.alt = true;
        else result.key = part;
    }
    return result;
}
function matchesCombo(e, parsed) {
    if (parsed.mod !== (e.metaKey || e.ctrlKey)) return false;
    if (parsed.shift !== e.shiftKey) return false;
    if (parsed.alt !== e.altKey) return false;
    return e.key.toLowerCase() === parsed.key;
}
/**
 * Generic keyboard-shortcut primitive for flow surfaces. Registers a single
 * combo on either the window or a DOM ref, with sensible defaults for the
 * two common flavors:
 *  - modeless global shortcut (e.g. `h` / `v`) → window, no preventDefault
 *  - editor-scoped modifier shortcut (e.g. `mod+z`) → ref, capture + preventDefault
 */ export function useKeyboardShortcut({ target = "window", combo, handler, ignoreWhenTyping = true, capture, preventDefault, enabled = true }) {
    useEffect(function subscribeToKeyboardShortcut() {
        if (!enabled) return;
        const parsed = parseCombo(combo);
        const hasModifier = parsed.mod || parsed.shift || parsed.alt;
        const useCapture = capture ?? hasModifier;
        const shouldPreventDefault = preventDefault ?? hasModifier;
        const node = target === "window" ? window : target.current;
        if (!node) return;
        function onKeyDown(e) {
            if (!matchesCombo(e, parsed)) return;
            if (ignoreWhenTyping && isTextInput(e.target)) return;
            if (shouldPreventDefault) {
                e.preventDefault();
                e.stopPropagation();
            }
            handler(e);
        }
        node.addEventListener("keydown", onKeyDown, {
            capture: useCapture
        });
        return ()=>{
            node.removeEventListener("keydown", onKeyDown, {
                capture: useCapture
            });
        };
    }, [
        target,
        combo,
        handler,
        ignoreWhenTyping,
        capture,
        preventDefault,
        enabled
    ]);
}

//# sourceMappingURL=useKeyboardShortcut.js.map